import { ColumnDef } from '@tanstack/react-table'
import { WithdrawTransactionResponse } from '../../types'
import { WithdrawStatus } from '@/schema'
import { Button } from '@/common/ui/button'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { SortingArrows } from '@/common/components/SortingArrows'
import { PlayerNoteColorModal } from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/PlayerNoteColorModal'
import { Badge } from '@/common/ui/badge'
import { PhoneCell } from '@/common/components/PhoneCell'
import WithdrawActions from './components/WithdrawActions'
import PlayerBankInfoCell from './components/PlayerBankInfoCell'
import ReceivingBankInfoCell from './components/ReceivingBankInfoCell'
import WithdrawErrorLogsButton from './components/WithdrawErrorLogsButton'

type WithdrawData = WithdrawTransactionResponse['data'][number]

const statusColors: Record<WithdrawStatus, string> = {
	PENDING: 'text-yellow-500',
	PROCESSING: 'text-yellow-500',
	COMPLETED: 'text-green-500',
	FAILED: 'text-red-500',
	CANCELED: 'text-red-500',
	EXPIRED: 'text-gray-500',
	REFUNDED: 'text-orange-500',
}

const statusLabels: Record<WithdrawStatus, string> = {
	PENDING: 'รอดำเนินการ',
	PROCESSING: 'กำลังดำเนินการ',
	COMPLETED: 'สำเร็จ',
	FAILED: 'ไม่สำเร็จ',
	CANCELED: 'ยกเลิก',
	EXPIRED: 'หมดอายุ',
	REFUNDED: 'คืนเงินแล้ว',
}

export const columns: ColumnDef<WithdrawData>[] = [
	{
		id: 'playerInfo',
		header: 'ข้อมูลผู้เล่น',
		cell: ({ row }) => {
			const player = row.original.player
			const playerId = row.original.playerId
			const color = player?.tags?.[0]?.color
			return (
				<div className="space-y-1">
					<p className="text-xs text-muted-foreground">{row.original.id}</p>
					{player ? (
						<div className="flex items-center gap-2">
							<PhoneCell playerId={playerId} phone={player.phone} />
							{color && (
								<div
									className="h-4 w-4 shrink-0 rounded-full border border-white shadow-sm"
									style={{ backgroundColor: color }}
									title={`สี: ${color}`}
								/>
							)}
						</div>
					) : (
						<span className="text-muted-foreground">-</span>
					)}
					{player && playerId && (
						<PlayerNoteColorModal
							player={{
								id: playerId,
								phone: player.phone,
								note: player.tags?.[0]?.name,
								color: player.tags?.[0]?.color,
							}}
						/>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'amount',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				className="px-0 hover:bg-transparent"
			>
				จำนวนเงิน
				<SortingArrows sortOrder={column.getIsSorted()} />
			</Button>
		),
		cell: ({ row }) => {
			const { amount, matchedAmount, refundAmount } = row.original as any
			const hasP2P = matchedAmount != null || refundAmount != null
			return (
				<div className="space-y-0.5">
					<span className="font-medium">฿{amount.toLocaleString()}</span>
					{hasP2P && (matchedAmount > 0 || refundAmount > 0) && (
						<div className="space-y-0 text-[10px]">
							{matchedAmount > 0 && (
								<p className="text-green-600">
									จับคู่แล้ว ฿{matchedAmount.toLocaleString()}
								</p>
							)}
							{refundAmount > 0 && (
								<p className="text-amber-600">
									คืนเงิน ฿{refundAmount.toLocaleString()}
								</p>
							)}
						</div>
					)}
				</div>
			)
		},
	},
	{
		id: 'bankDetails',
		header: 'ข้อมูลบัญชี',
		cell: ({ row }) => (
			<div className="space-y-2">
				<div>
					<p className="mb-0.5 text-xs text-muted-foreground">บัญชีผู้เล่น</p>
					<PlayerBankInfoCell row={row} />
				</div>
				<div>
					<p className="mb-0.5 text-xs text-muted-foreground">บัญชีรับเงิน</p>
					<ReceivingBankInfoCell row={row} />
				</div>
			</div>
		),
	},
	{
		id: 'processorStatus',
		header: 'การถอน',
		cell: ({ row }) => {
			const { status, processor, manualWithdrawOperator, isApprovedByAdmin } =
				row.original
			const operator = manualWithdrawOperator?.username

			const processorLabel =
				status === WithdrawStatus.EXPIRED
					? 'หมดอายุ'
					: processor === 'AUTO' && !isApprovedByAdmin
						? 'ถอนอัตโนมัติ'
						: `ทำรายการโดยพนักงาน${operator ? ` ${operator}` : ''}`

			const r = row.original as WithdrawData & {
				maintenanceDepositEndAt?: string | null
				maintenanceWithdrawEndAt?: string | null
			}
			const hasDeposit = !!r.maintenanceDepositEndAt
			const hasWithdraw = !!r.maintenanceWithdrawEndAt

			return (
				<div className="space-y-1">
					<span className={statusColors[status]}>{statusLabels[status]}</span>
					<p className="text-xs text-muted-foreground">{processorLabel}</p>
					{(hasDeposit || hasWithdraw) && (
						<div className="flex flex-wrap gap-1">
							{hasDeposit && (
								<Badge variant="secondary" className="text-xs">
									ซ่อมบำรุงฝาก
								</Badge>
							)}
							{hasWithdraw && (
								<Badge variant="secondary" className="text-xs">
									ซ่อมบำรุงถอน
								</Badge>
							)}
						</div>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'createdAt',
		header: ({ column }) => (
			<Button
				variant="ghost"
				onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
				className="px-0 hover:bg-transparent"
			>
				วันที่ถอน
				<SortingArrows sortOrder={column.getIsSorted()} />
			</Button>
		),
		cell: ({ row }) => {
			const note = row.original.note
			const status = row.original.status
			const failedMessage =
				'โปรดตรวจสอบผ่านผู้ให้บริการ Payment Gateway ถ้าไม่สำเร็จให้คืนเงินให้ลูกค้าตามจำนวนถอนแล้วให้ลูกค้าทำรายการถอนใหม่อีกครั้ง'
			const displayNote = status === 'FAILED' ? failedMessage : (note ?? null)

			return (
				<div className="space-y-1">
					<p className="text-sm">
						{format(new Date(row.original.createdAt || ''), 'PPP HH:mm น.', {
							locale: th,
						})}
					</p>
					{displayNote && (
						<p
							className={`max-w-[200px] truncate text-xs ${status === 'FAILED' ? 'text-red-600' : 'text-muted-foreground'}`}
							title={displayNote}
						>
							{displayNote}
						</p>
					)}
				</div>
			)
		},
	},
	{
		id: 'actions',
		header: 'การดำเนินการ',
		cell: ({ row }) => {
			const {
				id,
				status,
				processor,
				method,
				amount,
				playerBank,
				timestamp,
				bankInfo,
				playerId,
			} = row.original
			const errorCount =
				(row.original as { _count?: { paymentGatewayErrors?: number } })._count
					?.paymentGatewayErrors || 0

			return (
				<div className="flex flex-col gap-1.5">
					<WithdrawActions
						id={id}
						status={status}
						processor={processor}
						method={method}
						amount={amount}
						playerBankProvider={playerBank?.bankProvider}
						playerBankAccountNumber={playerBank?.accountNumber}
						playerBankAccountName={playerBank?.accountName}
						timestamp={timestamp}
						bankInfo={bankInfo}
					/>
					<div className="flex flex-wrap gap-1">
						<Link to={`/operator/transactions/withdraws/${id}`}>
							<Button variant="outline" size="sm">
								รายละเอียด
							</Button>
						</Link>
						<Link to={`/operator/get-player-list/player-detail/${playerId}`}>
							<Button variant="outline" size="sm">
								ผู้เล่น
							</Button>
						</Link>
					</div>
					<WithdrawErrorLogsButton id={id} errorCount={errorCount} />
				</div>
			)
		},
	},
]

export const processorOptions = [
	{
		label: 'ทั้งหมด',
		value: '',
	},
	{
		label: 'ถอนด้วยพนักงาน',
		value: 'MANUAL',
	},
	{
		label: 'ถอนอัตโนมัติ',
		value: 'AUTO',
	},
]

export const statusOptions = [
	{
		label: 'กำลังดำเนินการ',
		value: WithdrawStatus.PENDING,
	},
	{
		label: 'สำเร็จ',
		value: WithdrawStatus.COMPLETED,
	},
	{
		label: 'ไม่สำเร็จ',
		value: WithdrawStatus.FAILED,
	},
	{
		label: 'ยกเลิก',
		value: WithdrawStatus.CANCELED,
	},
	{
		label: 'หมดเวลา',
		value: WithdrawStatus.EXPIRED,
	},
]

export const defaultStatusValues = [WithdrawStatus.PENDING]

export const playerBankTypeOptions = [
	{ label: 'ทั้งหมด', value: '' },
	{ label: 'ธนาคาร', value: 'BANK' },
	{ label: 'ทรู', value: 'TRUE_MONEY' },
]
